import { PageHead } from '../../components/ui/PageHead';
import { PageHero } from '../../components/hero/PageHero';
import { Section } from '../../components/layout/Section';
import { Container } from '../../components/layout/Container';
import { Grid } from '../../components/layout/Grid';
import { Stack } from '../../components/layout/Stack';
import { PillarCard } from '../../components/cards/PillarCard';
import { UseCaseCard } from '../../components/cards/UseCaseCard';
import { DiagramBlock } from '../../components/content/DiagramBlock';
import { StatsBar } from '../../components/content/StatsBar';
import { TechLogoGrid } from '../../components/content/TechLogoGrid';
import { CTABand } from '../../components/cta/CTABand';
import styles from './PlatformPage.module.css';

const pillars = [
  {
    icon: '🏛️',
    title: 'Medallion Lakehouse',
    description:
      'Cloud-agnostic Bronze, Silver, Gold data architecture with automated quality enforcement, schema evolution, and full data lineage from ingestion to consumption.',
    points: [
      'Delta Lake & Apache Iceberg',
      'Automated data quality gates',
      'Schema registry & evolution',
      'Full column-level lineage',
    ],
  },
  {
    icon: '🤖',
    title: 'Agentic AI Systems',
    description:
      'Autonomous multi-agent pipelines that monitor, orchestrate, and optimize your data platform continuously - without human intervention.',
    points: [
      'Pipeline orchestration agents',
      'Anomaly detection & self-healing',
      'Natural language data queries',
      'Autonomous SLA management',
    ],
  },
  {
    icon: '🔬',
    title: 'ML Lifecycle & MLOps',
    description:
      'Production-grade model governance with experiment tracking, model registry, CI/CD pipelines, and automated drift detection.',
    points: [
      'MLflow & Kubeflow integration',
      'Model versioning & governance',
      'Feature store management',
      'Champion/challenger workflows',
    ],
  },
  {
    icon: '🛡️',
    title: 'Governance & Compliance',
    description:
      'Enterprise data governance with automated cataloging, fine-grained access controls, and compliance posture for regulated industries.',
    points: [
      'Unity Catalog & Apache Atlas',
      'Row/column-level security',
      'HIPAA, SOX, GDPR controls',
      'Automated compliance reporting',
    ],
  },
  {
    icon: '⚙️',
    title: 'Infrastructure & DevOps',
    description:
      'IaC-first platform deployment with Terraform, Kubernetes, and full CI/CD automation for zero-downtime lakehouse operations.',
    points: [
      'Terraform & Pulumi IaC',
      'Kubernetes orchestration',
      'Blue/green deployments',
      'Cost optimization automation',
    ],
  },
  {
    icon: '📊',
    title: 'Observability',
    description:
      'End-to-end platform observability with data quality dashboards, pipeline health monitoring, and proactive alerting.',
    points: [
      'Data quality scoring',
      'Pipeline SLA monitoring',
      'Cost attribution analytics',
      'Executive health dashboards',
    ],
  },
];

const useCases = [
  { tag: 'Data Engineering', title: 'Real-time CDC ingestion from 50+ source systems', description: 'Unified streaming and batch ingestion with sub-minute latency.' },
  { tag: 'Analytics', title: 'Self-service analytics on Gold-layer curated datasets', description: 'Business teams query governed data without engineering bottlenecks.' },
  { tag: 'ML / AI', title: 'Automated feature engineering pipeline for fraud models', description: '80% reduction in feature development time with automated profiling.' },
  { tag: 'Governance', title: 'Automated PII detection and masking at Bronze ingestion', description: 'Zero-touch compliance enforcement across all incoming data streams.' },
  { tag: 'Agentic AI', title: 'Autonomous pipeline repair and reprocessing agents', description: 'Self-healing infrastructure reduces incident MTTR from hours to minutes.' },
  { tag: 'MLOps', title: 'Shadow mode champion/challenger model evaluation', description: 'Production-safe model A/B testing with automated promotion gates.' },
];

export function PlatformPage() {
  return (
    <>
      <PageHead
        title="Platform"
        description="NAATS Medallion Lakehouse platform - unified data architecture, Agentic AI, ML lifecycle, and enterprise governance in one cloud-agnostic solution."
      />

      <PageHero
        eyebrow="The NAATS Platform"
        title="Architecture for Enterprise AI at Scale"
        description="A unified, cloud-agnostic Medallion Lakehouse with Agentic AI, production-grade MLOps, and enterprise governance - all in one integrated platform."
      />

      <StatsBar />

      {/* Medallion Architecture Diagram */}
      <Section bg="page">
        <Container>
          <Stack gap="xl" align='center'>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Architecture</p>
              <h2 className={styles.sectionTitle}>The Medallion Lakehouse Model</h2>
              <p className={styles.sectionDesc}>
                Three structured data layers - each with embedded quality, governance, and AI
                readiness - transforming raw chaos into business-ready intelligence.
              </p>
            </div>
            <DiagramBlock type="medallion" />
          </Stack>
        </Container>
      </Section>

      {/* Platform Pillars */}
      <Section bg="subtle">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Capabilities</p>
              <h2 className={styles.sectionTitle}>Six Integrated Platform Pillars</h2>
            </div>
            <Grid cols={3} gap="md">
              {pillars.map((p) => (
                <PillarCard key={p.title} {...p} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Agentic AI Diagram */}
      <Section bg="page">
        <Container>
          <Stack gap="xl" align='center'>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Agentic AI</p>
              <h2 className={styles.sectionTitle}>Autonomous Data Intelligence</h2>
              <p className={styles.sectionDesc}>
                Six specialized agents working in concert - continuously monitoring, optimizing,
                and governing your platform without human intervention.
              </p>
            </div>
            <DiagramBlock type="agentic" />
          </Stack>
        </Container>
      </Section>

      {/* Use Cases */}
      <Section bg="subtle">
        <Container>
          <Stack gap="xl">
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Use Cases</p>
              <h2 className={styles.sectionTitle}>What Teams Build on NAATS</h2>
            </div>
            <Grid cols={3} gap="sm">
              {useCases.map((uc) => (
                <UseCaseCard key={uc.title} {...uc} />
              ))}
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Tech Stack */}
      <Section bg="page">
        <Container>
          <TechLogoGrid />
        </Container>
      </Section>

      <CTABand
        heading="Ready to Build Your Medallion Lakehouse?"
        subheading="Schedule a deep-dive with a NAATS expert to explore our full range of solutions."
      />
    </>
  );
}
